import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, MessageCircle, Copy, MapPin, CheckCircle2, Truck, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { formatINR, INDIAN_STATES, resolveWhatsAppNumber, priceOf } from "@/config";
import { api, formatApiError } from "@/lib/api";
import { itemConfigurationError, itemDetailsText } from "@/lib/productDetails";
import { currentCampaignId } from "@/lib/campaign";
import { ItemDetailsSummary } from "./ItemDetailsSummary";
import { CartItemEditor } from "./CartItemEditor";

function buildMessage(items, total, name, phone, state, notes, address, city, pincode, gstin, discount = 0, discountLabel = "", couponCode = "", couponDiscount = 0) {
  const lines = [
    "NEW ORDER — VARSHA SUBLIMATION",
    "--------------------------",
    ...items.map(
      (i) => `• ${i.name}${i.sku ? ` [Code: ${i.sku}]` : ""} x${i.qty} — ${formatINR(i.price * i.qty)}${itemDetailsText(i) ? `\n${itemDetailsText(i)}` : ""}`
    ),
    "--------------------------",
    ...(discount > 0 ? [`${discountLabel}: − ${formatINR(discount)}`] : []),
    ...(couponDiscount > 0 ? [`Code ${couponCode}: − ${formatINR(couponDiscount)}`] : []),
    `TOTAL: ${formatINR(total)}`,
    "",
    `Name: ${name}`,
    `Phone: ${phone}`,
    "",
    "DELIVERY ADDRESS",
    address || "—",
    `${city || "—"} — ${pincode || "—"}`,
    state,
  ];
  if (gstin) lines.push(`GSTIN: ${gstin}`);
  if (notes.trim()) lines.push(`Notes: ${notes}`);
  return lines.join("\n");
}

export default function OrderDrawer() {
  const { items, total, updateQty, removeItem, updateItemDetails, clear, drawerOpen, setDrawerOpen, syncStatus, cartReady, retrySync, checkoutTracking } = useCart();
  const { customer, setCustomer, signOut } = useCustomer();
  const [name, setName] = useState(customer?.full_name || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [state, setState] = useState(customer?.state || "");
  const [address, setAddress] = useState(customer?.address || "");
  const [city, setCity] = useState(customer?.city || "");
  const [pincode, setPincode] = useState(customer?.pincode || "");
  const [gstin, setGstin] = useState(customer?.gstin || "");
  const [notes, setNotes] = useState("");
  const [routes, setRoutes] = useState([]);
  const [sending, setSending] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const editingItem = items.find((item) => item.product.id === editingId);

  useEffect(() => {
    document.body.style.overflow = drawerOpen || placed ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen, placed]);

  useEffect(() => {
    if (!drawerOpen) { setEditingId(null); return; }
    api.get("/whatsapp-routes").then((r) => setRoutes(r.data)).catch(() => setRoutes([]));
  }, [drawerOpen]);

  const [quote, setQuote] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState("");
  const [publicOffer, setPublicOffer] = useState(null);
  const [qtyDraft, setQtyDraft] = useState({});

  useEffect(() => {
    api.get("/offers/code").then(({ data }) => setPublicOffer(data.code ? data : null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!drawerOpen || !items.length) { setQuote(null); return; }
    const payload = { code, phone: (phone || customer?.phone || "").trim(), items: items.map((i) => ({ product_id: i.product.id, sku: i.product.sku || "", name: i.product.name, price: priceOf(i.product), qty: i.qty, size_quantities: i.size_quantities || {}, details: i.details || "" })) };
    let live = true;
    api.post("/orders/quote", payload)
      .then(({ data }) => { if (live) setQuote(data); })
      .catch(() => { if (live) setQuote(null); });
    return () => { live = false; };
  }, [drawerOpen, items, code, phone, customer]);

  const payable = quote ? quote.total : total;
  const lineItems = items.map((i) => ({ name: i.product.name, sku: i.product.sku || "", price: priceOf(i.product), qty: i.qty, size_quantities: i.size_quantities || {}, details: i.details || "" }));
  const message = buildMessage(lineItems, payable, name || "—", phone || "—", state || "—", notes, address, city, pincode, gstin, quote?.discount || 0, quote?.discount_label || "", quote?.coupon_code || "", quote?.coupon_discount || 0);

  const placeOrder = async () => {
    if (!items.length) return toast.error("Your order is empty");
    if (!name.trim() || !phone.trim()) return toast.error("Add your name and phone so we can confirm");
    if (!state) return toast.error("Select your state so we connect you to the right sales desk");
    if (address.trim().length < 10) return toast.error("Add the full delivery address");
    if (!city.trim()) return toast.error("Add your city");
    if (pincode.replace(/\D/g, "").length !== 6) return toast.error("Pincode must be 6 digits");
    if (gstin.trim() && gstin.trim().length !== 15) return toast.error("GST number must be 15 characters — or leave it blank");
    setSending(true);
    try {
      const tracking = await checkoutTracking();
      const { data } = await api.post("/orders", {
        cart_id: tracking.cart_id,
        campaign_id: currentCampaignId(),
        coupon_code: quote?.coupon_code || "",
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        state,
        address: address.trim(),
        city: city.trim(),
        pincode: pincode.replace(/\D/g, ""),
        gstin: gstin.trim().toUpperCase(),
        notes: notes.trim(),
        items: tracking.items.map((i) => ({
          product_id: i.product.id,
          sku: i.product.sku || "",
          name: i.product.name,
          price: priceOf(i.product),
          qty: i.qty,
          size_quantities: i.size_quantities || {},
          details: i.details || "",
        })),
        total: tracking.total,
      }, { headers: tracking.headers });
      if (customer) {
        setCustomer((current) => ({ ...current, address: address.trim(), city: city.trim(), pincode: pincode.replace(/\D/g, ""), gstin: gstin.trim().toUpperCase() }));
      }
      const number = resolveWhatsAppNumber(routes, state);
      const savedMessage = buildMessage(data.items, data.total, data.customer_name, data.customer_phone, data.state, data.notes, data.address, data.city, data.pincode, data.gstin, data.discount, data.discount_label, data.coupon_code, data.coupon_discount);
      setPlaced({ order: data, waLink: `https://wa.me/${number}?text=${encodeURIComponent(savedMessage)}` });
      clear({ submitted: true });
      setDrawerOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const copyMessage = async () => {
    await navigator.clipboard.writeText(message);
    toast.success("Order text copied");
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { if (!sending) setDrawerOpen(false); }}
            className="fixed inset-0 z-[60] bg-[#0A0B0E]/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 34 }}
            data-testid="whatsapp-order-drawer"
            className="fixed right-0 top-0 z-[65] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#12141A]"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">WhatsApp</div>
                <h3 className="font-display text-xl font-bold">Order Builder</h3>
              </div>
              <button
                data-testid="close-order-drawer-btn"
                disabled={sending}
                onClick={() => setDrawerOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-fuchsia-400 hover:text-fuchsia-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {(items.length > 0 || syncStatus !== "saved") && (
                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="cart-sync-status" role="status">
                  {syncStatus === "restoring" ? "Restoring your saved cart…" : syncStatus === "saving" ? "Syncing cart…" : syncStatus === "saved" ? "Cart saved" : syncStatus === "signin" ? "Sign in again to restore and save your cart." : items.length ? "Your items are kept on this device. Online sync pending." : "Could not restore your saved cart. Please retry."}
                  {["offline", "signin"].includes(syncStatus) && <button data-testid="cart-sync-retry" disabled={sending} onClick={syncStatus === "signin" ? signOut : retrySync} className="min-h-9 rounded-full border border-cyan-400/30 px-3 text-cyan-300 transition-colors hover:bg-cyan-400/10">{syncStatus === "signin" ? "Sign in" : "Retry"}</button>}
                </div>
              )}
              {!cartReady && !items.length ? (
                <div data-testid="cart-restore-loading" role="status" className="flex items-center justify-center gap-3 py-12 text-sm text-slate-300"><Loader2 size={18} className="animate-spin" /> Restoring your products…</div>
              ) : items.length === 0 && syncStatus === "saved" ? (
                <div data-testid="order-empty-state" className="mt-16 text-center text-sm text-slate-500">
                  Your order is empty.
                  <br />Add blanks from the catalog to begin.
                </div>
              ) : items.length > 0 ? (
                <div className="space-y-3">
                  {items.map((i) => (
                    <div key={i.product.id} data-testid={`order-item-${i.product.id}`} className="flex gap-3 rounded-xl border border-white/10 bg-[#0A0B0E]/60 p-3">
                      <img src={i.product.image || "/placeholder-product.svg"} alt={i.product.name} className="h-16 w-16 shrink-0 rounded-lg bg-white object-contain" onError={(event) => { event.currentTarget.src = "/placeholder-product.svg"; }} />
                      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div data-testid={`order-item-name-${i.product.id}`} className="text-sm font-semibold">{i.product.name}</div>
                            {i.product.sku && (
                              <div data-testid={`order-item-sku-${i.product.id}`} className="font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300">
                                Code {i.product.sku}
                              </div>
                            )}
                          </div>
                          <button data-testid={`order-remove-${i.product.id}`} disabled={sending} onClick={() => removeItem(i.product.id)} className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-500 hover:text-red-400 disabled:opacity-40">
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {i.product.unavailable && <p data-testid={`order-item-unavailable-${i.product.id}`} className="mt-2 text-xs text-rose-300">This product is no longer available. Remove it to continue.</p>}
                        <ItemDetailsSummary item={i} prefix={`order-item-${i.product.id}`} />
                        {itemConfigurationError(i) && <p data-testid={`order-item-configuration-${i.product.id}`} className="mt-2 text-xs text-amber-300">{itemConfigurationError(i)}</p>}
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                          {i.product.size_options?.length || Object.keys(i.size_quantities || {}).length ? (
                            <span data-testid={`order-item-qty-${i.product.id}`} className="text-xs text-slate-300">Qty {i.qty}</span>
                          ) : <div className="flex items-center gap-1 rounded-full border border-white/10 px-1.5 py-0.5">
                            <button data-testid={`order-qty-minus-${i.product.id}`} disabled={sending} onClick={() => updateQty(i.product.id, i.qty - 1)} className="flex h-8 w-8 items-center justify-center text-slate-400 hover:text-cyan-300 disabled:opacity-40"><Minus size={12} /></button>
                            <input
                              data-testid={`order-item-qty-${i.product.id}`}
                              aria-label={`${i.product.name} quantity`}
                              type="number"
                              min="1"
                              max="99999"
                              inputMode="numeric"
                              disabled={sending}
                              value={qtyDraft[i.product.id] ?? i.qty}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "").slice(0, 5);
                                setQtyDraft({ ...qtyDraft, [i.product.id]: raw });
                                if (raw) updateQty(i.product.id, Math.max(1, Number(raw)));
                              }}
                              onBlur={() => {
                                if (!qtyDraft[i.product.id]) updateQty(i.product.id, Math.max(1, i.qty));
                                const { [i.product.id]: _drop, ...rest } = qtyDraft;
                                setQtyDraft(rest);
                              }}
                              className="h-8 w-14 rounded-full bg-transparent text-center font-mono text-xs text-slate-100 focus:bg-[#0A0B0E] focus:outline-none disabled:opacity-40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <button data-testid={`order-qty-plus-${i.product.id}`} disabled={sending} onClick={() => updateQty(i.product.id, i.qty + 1)} className="flex h-8 w-8 items-center justify-center text-slate-400 hover:text-cyan-300 disabled:opacity-40"><Plus size={12} /></button>
                          </div>}
                          <div data-testid={`order-item-line-total-${i.product.id}`} className="font-mono text-sm text-cyan-300">{formatINR(priceOf(i.product) * i.qty)}</div>
                        </div>
                        <button data-testid={`order-edit-details-${i.product.id}`} disabled={sending || i.product.unavailable} onClick={() => setEditingId(i.product.id)} className="mt-2 flex min-h-9 items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs text-cyan-300 transition-colors hover:border-cyan-400/40 hover:bg-cyan-400/5 disabled:opacity-40"><Pencil size={12} />{i.product.size_options?.length ? "Edit sizes & details" : "Edit product details"}</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <fieldset data-testid="order-details-fields" disabled={sending} aria-busy={sending} className="mt-6 min-w-0 space-y-3">
                <input
                  data-testid="order-customer-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                />
                <input
                  data-testid="order-customer-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Your phone number"
                  className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                />
                <select
                  data-testid="order-customer-state-select"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm text-slate-200 focus:border-cyan-400/50 focus:outline-none"
                >
                  <option value="">Select your state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <div className="pt-2">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-400">
                    <MapPin size={12} /> Delivery address
                  </div>
                  <div className="mt-3 space-y-3">
                    <textarea
                      data-testid="order-address-input"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Shop / building, street, area"
                      rows={2}
                      className="w-full resize-none rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        data-testid="order-city-input"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                        className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                      />
                      <input
                        data-testid="order-pincode-input"
                        value={pincode}
                        onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        placeholder="Pincode"
                        className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                      />
                    </div>
                    <input
                      data-testid="order-gstin-input"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))}
                      placeholder="GST number (optional)"
                      className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 font-mono text-sm uppercase placeholder:font-sans placeholder:normal-case placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                    />
                  </div>
                </div>

                <textarea
                  data-testid="order-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes (e.g. custom sizes, logo printing request)"
                  rows={3}
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 text-sm placeholder:text-slate-600 focus:border-fuchsia-400/50 focus:outline-none"
                />
              </fieldset>
            </div>

            <div className="border-t border-white/10 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
              <div className="mb-4 flex gap-2">
                <input
                  data-testid="coupon-code-input"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="Discount code"
                  className="h-11 flex-1 rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 font-mono text-sm uppercase tracking-[0.15em] placeholder:tracking-normal placeholder:text-slate-600 focus:border-yellow-300/60 focus:outline-none"
                />
                <button
                  data-testid="coupon-apply-btn"
                  onClick={() => (code ? (setCode(""), setCodeInput("")) : setCode(codeInput.trim()))}
                  disabled={!code && !codeInput.trim()}
                  className="h-11 rounded-xl border border-yellow-300/40 bg-yellow-300/10 px-5 text-xs font-bold uppercase tracking-[0.15em] text-yellow-300 disabled:opacity-40"
                >
                  {code ? "Remove" : "Apply"}
                </button>
              </div>
              {quote?.coupon_error && (
                <p className="mb-3 text-xs text-red-300" data-testid="coupon-error">{quote.coupon_error}</p>
              )}
              {customer?.welcome_code && !quote?.coupon_discount && (
                <button
                  data-testid="welcome-code-hint"
                  onClick={() => { setCodeInput(customer.welcome_code); setCode(customer.welcome_code); }}
                  className="mb-3 w-full rounded-xl border border-yellow-300/30 bg-yellow-300/5 px-3 py-2 text-left text-[11px] text-yellow-200"
                >
                  Your welcome code <span className="font-mono font-bold tracking-[0.15em]">{customer.welcome_code}</span> — tap to use it on this order
                </button>
              )}
              {!customer?.welcome_code && publicOffer?.code && !quote?.coupon_discount && (
                <button
                  data-testid="public-code-hint"
                  onClick={() => { setCodeInput(publicOffer.code); setCode(publicOffer.code); }}
                  className="mb-3 w-full rounded-xl border border-yellow-300/30 bg-yellow-300/5 px-3 py-2 text-left text-[11px] text-yellow-200"
                >
                  {publicOffer.percent}% off above {publicOffer.min_total_text} with code{" "}
                  <span className="font-mono font-bold tracking-[0.15em]">{publicOffer.code}</span> — tap to apply
                </button>
              )}
              <div className="mb-4 space-y-1.5 font-mono text-sm">
                {(quote?.discount > 0 || quote?.coupon_discount > 0) && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-[0.2em] text-slate-500">Items</span>
                    <span className="text-slate-400" data-testid="order-subtotal-value">{formatINR(quote.subtotal)}</span>
                  </div>
                )}
                {quote?.discount > 0 && (
                  <div className="flex items-center justify-between text-xs" data-testid="order-discount-row">
                    <span className="uppercase tracking-[0.15em] text-[#F5C45A]">{quote.discount_label}</span>
                    <span className="text-[#F5C45A]" data-testid="order-discount-value">− {formatINR(quote.discount)}</span>
                  </div>
                )}
                {quote?.coupon_discount > 0 && (
                  <div className="flex items-center justify-between text-xs" data-testid="order-coupon-row">
                    <span className="uppercase tracking-[0.15em] text-yellow-300">{quote.coupon_label}</span>
                    <span className="text-yellow-300" data-testid="order-coupon-value">− {formatINR(quote.coupon_discount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="uppercase tracking-[0.2em] text-slate-500">Total</span>
                  <span data-testid="order-total-value" className="text-xl font-bold text-yellow-300">{formatINR(payable)}</span>
                </div>
              </div>
              <button
                data-testid="submit-whatsapp-order-btn"
                onClick={placeOrder}
                disabled={sending || !cartReady || items.length === 0 || items.some((item) => item.product.unavailable || itemConfigurationError(item))}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-sm font-bold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-40"
              >
                <MessageCircle size={16} /> {sending ? "Placing order..." : "Place Order"}
              </button>
              <button
                data-testid="copy-order-text-btn"
                onClick={copyMessage}
                disabled={sending || items.length === 0}
                className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300 disabled:opacity-40"
              >
                <Copy size={14} /> Copy Order Text
              </button>
            </div>
          </motion.aside>
        </>
      )}
      {drawerOpen && editingItem && <CartItemEditor key={editingId} item={editingItem} onSave={(values) => updateItemDetails(editingId, values)} onClose={() => setEditingId(null)} />}
      {placed && (
        <OrderPlacedModal
          order={placed.order}
          waLink={placed.waLink}
          onClose={() => setPlaced(null)}
        />
      )}
    </AnimatePresence>
  );
}

function OrderPlacedModal({ order, waLink, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="order-placed-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0A0B0E]/85 p-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.92, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#12141A] p-7 text-center"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-400/10">
          <CheckCircle2 size={26} className="text-emerald-300" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-bold" data-testid="order-placed-title">Order received</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400" data-testid="order-placed-message">
          Thank you, {order.customer_name?.split(" ")[0] || "friend"}. Our team will confirm this order on
          <span className="text-emerald-300"> WhatsApp ({order.customer_phone})</span> with availability and dispatch time.
        </p>
        <div className="mt-5 rounded-2xl border border-white/10 bg-[#0A0B0E]/60 p-4 text-left">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Delivering to</div>
          <p className="mt-1.5 text-sm text-slate-300" data-testid="order-placed-address">
            {order.address}
            <br />{order.city} — {order.pincode}
            <br />{order.state}
            {order.gstin && <><br /><span className="font-mono text-xs text-cyan-300">GSTIN {order.gstin}</span></>}
          </p>
          {order.discount > 0 && (
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 font-mono text-xs" data-testid="order-placed-discount">
              <span className="uppercase tracking-[0.15em] text-[#F5C45A]">{order.discount_label}</span>
              <span className="text-[#F5C45A]">− {formatINR(order.discount)}</span>
            </div>
          )}
          {order.coupon_discount > 0 && (
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 font-mono text-xs" data-testid="order-placed-coupon">
              <span className="uppercase tracking-[0.15em] text-yellow-300">Code {order.coupon_code}</span>
              <span className="text-yellow-300">− {formatINR(order.coupon_discount)}</span>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 font-mono text-xs">
            <span className="uppercase tracking-[0.2em] text-slate-500">Order total</span>
            <span className="font-bold text-yellow-300" data-testid="order-placed-total">{formatINR(order.total)}</span>
          </div>
        </div>
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="order-placed-whatsapp-btn"
          onClick={onClose}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3.5 text-sm font-bold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
        >
          <MessageCircle size={16} /> Send it on WhatsApp now
        </a>
        <button
          data-testid="order-placed-track-btn"
          onClick={() => { onClose(); window.dispatchEvent(new CustomEvent("vs:my-orders")); }}
          className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-full border border-cyan-400/40 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-cyan-300 transition-colors hover:bg-cyan-400/10"
        >
          <Truck size={14} /> Track this order
        </button>
        <button
          data-testid="order-placed-close-btn"
          onClick={onClose}
          className="mt-2.5 w-full rounded-full border border-white/15 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
        >
          I'll wait for your WhatsApp
        </button>
      </motion.div>
    </motion.div>
  );
}
